// components/PasskeyEnrollmentPrompt.tsx
// Modal prompting users to set up a passkey after successful email + OTP authentication
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import DevicesIcon from '@mui/icons-material/Devices';
import { PasskeyService } from '../services/PasskeyService';
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/types';

interface PasskeyEnrollmentPromptProps {
  open: boolean;
  onClose: () => void;
  onEnrolled: () => void;
}

const DISMISS_COOKIE_NAME = 'passkey_prompt_dismissed';
const DISMISS_DURATION_DAYS = 30;

const PasskeyEnrollmentPrompt: React.FC<PasskeyEnrollmentPromptProps> = ({
  open,
  onClose,
  onEnrolled,
}) => {
  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Pre-fetch registration options when the prompt opens so the WebAuthn ceremony
  // can start synchronously from the click — Safari loses user activation if a
  // network request is awaited between the click and create().
  const [registrationOptions, setRegistrationOptions] = useState<PublicKeyCredentialCreationOptionsJSON | null>(null);

  useEffect(() => {
    if (!open) {
      setRegistrationOptions(null);
      return;
    }
    let cancelled = false;
    PasskeyService.fetchRegistrationOptions().then((result) => {
      if (cancelled) return;
      if (result.success && result.options) {
        setRegistrationOptions(result.options);
      } else {
        setError(result.message || 'Failed to start registration');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleEnroll = async () => {
    if (!registrationOptions) return;

    setEnrolling(true);
    setError(null);

    try {
      const result = await PasskeyService.completeRegistration(registrationOptions);

      if (result.success) {
        onEnrolled();
        onClose();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setEnrolling(false);
    }
  };

  const handleRemindLater = () => {
    // Set cookie to not show this prompt for 30 days
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + DISMISS_DURATION_DAYS);
    document.cookie = `${DISMISS_COOKIE_NAME}=true; expires=${expiryDate.toUTCString()}; path=/; SameSite=Strict`;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Only allow closing via explicit button clicks, not backdrop or escape
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        handleRemindLater();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <FingerprintIcon color="primary" fontSize="large" />
          <Typography variant="h5">Secure Your Account with a Passkey</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" paragraph>
          Passkeys are a faster and more secure way to sign in. Instead of waiting for an email,
          you can use your fingerprint, face, or device PIN.
        </Typography>

        <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
          Benefits:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <SpeedIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Instant sign-in with biometrics" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <SecurityIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="More secure than passwords" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <DevicesIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Works across all your devices" />
          </ListItem>
        </List>

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleRemindLater} disabled={enrolling}>
          Remind Me Later
        </Button>
        <Button
          variant="contained"
          onClick={handleEnroll}
          disabled={enrolling || !registrationOptions}
          startIcon={enrolling ? <CircularProgress size={20} /> : <FingerprintIcon />}
        >
          {enrolling ? 'Setting Up...' : 'Set Up Passkey'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasskeyEnrollmentPrompt;

/**
 * Helper function to check if the enrollment prompt should be shown
 * Returns false if the user has dismissed the prompt within the last 30 days
 */
export function shouldShowPasskeyPrompt(): boolean {
  // Check if user has dismissed the prompt
  const cookies = document.cookie.split(';');
  const dismissed = cookies.some(cookie =>
    cookie.trim().startsWith(`${DISMISS_COOKIE_NAME}=true`)
  );

  return !dismissed;
}
